import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MiniDrawer from "../../../components/MiniDrawer/MiniDrawer";
import { useNavigate } from "react-router-dom";
import filterIcon from "../../../Icons/filterIcon.png";
import ExcelExport from "../../../components/ExcelExport/ExcelExportShipmentExecution";
import axios from "axios";
import CryptoJS from "crypto-js";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";

const ShipmentExecutionSheet = () => {
  const navigate = useNavigate();
  const baseAPI = process.env.REACT_APP_API_URL;

  // ---------- unified filters state ----------
  const today = new Date();
  //const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);


  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  const firstDayOfMonth = currentMonth < 3
    ? new Date(currentYear - 1, 3, 1)
    : new Date(currentYear, 3, 1);



  const initialFilters = {
    searchParam: "",
    dateAttribute: "1", // Invoice Date default
    startDate: firstDayOfMonth,
    endDate: today,
    serviceType: "0",
    vendor: "",
    petCount: "",
    chargeable_wt: "",
    leadOwner: "",
    opsCoordinator: "",
    orderStatus: "-1",
    shipmentStatus: "",
    leadSource: "",
    region: "",
    originCountry: "",
    originState: "",
    originCity: "",
    destinationCountry: "",
    destinationState: "",
    destinationCity: "",
    leadType: "",
    fileStatus: "-1",
  };

  const [filters, setFilters] = useState(initialFilters);

  // date-range picker UI state
  const [selectionRange, setSelectionRange] = useState({
    startDate: firstDayOfMonth,
    endDate: today,
    key: "selection",
  });
  const [dateRangeLabel, setDateRangeLabel] = useState(
    `${formatDateDisplay(firstDayOfMonth)} - ${formatDateDisplay(today)}`
  );
  const [pickerOpen, setPickerOpen] = useState(false);

  // results & supporting lists
  const [expenseDetailResponse, setExpenseDetailResponse] = useState([]);
  const [recordCount, setRecordCount] = useState(0);
  const [serviceTypeResponse, setServiceTypeResponse] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [assignToResponse, setAssignToResponse] = useState([]);
  const [assignToLOResponse, setAssignToLOResponse] = useState([]);
  const [originCountryResponse, setOriginCountryResponse] = useState([]);
  const [originStateResponse, setOriginStateResponse] = useState([]);
  const [originCityResponse, setOriginCityResponse] = useState([]);
  const [destinationStateResponse, setDestinationStateResponse] = useState([]);
  const [destinationCityResponse, setDestinationCityResponse] = useState([]);
  const [leadSourceResponse, setLeadSourceResponse] = useState([]);

  // ---------- helpers ----------
  function getStoredToken() {
    // original code used JSON.parse(localStorage.getItem("token"))
    // keep same approach — if token is an object/string, adjust if needed
    try {
      return JSON.parse(localStorage.getItem("token"));
    } catch (err) {
      return localStorage.getItem("token");
    }
  }

  function formatDateForApi(date) {
    // same format you used before (e.g., "08 Jun 2025")
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  function formatDateDisplay(date) {
    if (!date) return "N/A";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  function formatDateWithTime(dateString) {
    if (!dateString) return "N/A";
    const dateObj = new Date(dateString);
    if (isNaN(dateObj)) return "Invalid date";
    const formatted = dateObj
      .toLocaleString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      })
      .replace(",", "");
    return formatted;
  }

  // ---------- API fetchers (reusable) ----------
  const fetchServiceTypes = async () => {
    const token = getStoredToken();
    try {
      const response = await axios.get(`${baseAPI}adminget/GetServiceType`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setServiceTypeResponse(response.data.data || []);
    } catch (err) {
      console.error("Error fetching service types:", err);
    }
  };

  const fetchVendors = async () => {
    const token = getStoredToken();
    try {
      const response = await axios.get(
        `${baseAPI}adminGet/GETSupplier?SupplierID=0`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVendorList(response.data.data || []);
    } catch (err) {
      console.error("Error fetching vendors:", err);
    }
  };

  const fetchEmployeesByDepartment = async (departmentId, setter) => {
    const token = getStoredToken();
    try {
      const response = await axios.get(
        `${baseAPI}adminget/GETEmployeeByDepartment?department_id=${departmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setter(response.data.data || []);
    } catch (err) {
      console.error("Error fetching employees for dept", departmentId, err);
    }
  };

  const fetchLeadSources = async () => {
    const token = getStoredToken();
    try {
      const response = await axios.get(
        `${baseAPI}adminget/GETLeadSource?LeadSourceID=0`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeadSourceResponse(response.data.data || []);
    } catch (err) {
      console.error("Error fetching lead sources:", err);
    }
  };

  const fetchCountries = async () => {
    const token = getStoredToken();
    try {
      const response = await axios.get(`${baseAPI}adminget/GETCountry?countryID=0`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOriginCountryResponse(response.data.data || []);
    } catch (err) {
      console.error("Error fetching countries:", err);
    }
  };

  // generic state fetcher used for origin/destination
  const fetchStates = async (countryId, type = "origin") => {
    const token = getStoredToken();
    if (!countryId) return;
    try {
      const response = await axios.get(
        `${baseAPI}adminget/GETState?stateID=0&countryID=${countryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (type === "origin") setOriginStateResponse(response.data.data || []);
      else setDestinationStateResponse(response.data.data || []);
    } catch (err) {
      console.error("Error fetching states:", err);
    }
  };

  const fetchCities = async (stateId, type = "origin") => {
    const token = getStoredToken();
    if (!stateId) return;
    try {
      const response = await axios.get(
        `${baseAPI}adminget/GETCity?cityID=0&stateID=${stateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (type === "origin") setOriginCityResponse(response.data.data || []);
      else setDestinationCityResponse(response.data.data || []);
    } catch (err) {
      console.error("Error fetching cities:", err);
    }
  };

  // ---------- main list fetch (used on mount and on Apply/Search) ----------
  const fetchShipmentList = async (overrides = {}) => {
    const token = getStoredToken();

    // Use current filters + any overrides passed
    const f = { ...filters, ...overrides };

    // If the user set custom dates via date picker, we send formatted dates; if the UI shows "Custom Dates" empty them
    let startDateParam = formatDateForApi(selectionRange.startDate);
    let endDateParam = formatDateForApi(selectionRange.endDate);

    if (dateRangeLabel === "Custom Dates") {
      startDateParam = "";
      endDateParam = "";
    }

    const queryParams = new URLSearchParams({
      search_param: f.searchParam || "",
      date_attribute: f.dateAttribute || "1",
      startDate: startDateParam,
      enddate: endDateParam,
      service_type: f.serviceType || "",
      vendor: f.vendor || "",
      petCount: f.petCount || "",
      chargeable_wt: f.chargeable_wt || "",
      lead_owner: f.leadOwner || "",
      ops_coordinator: f.opsCoordinator || "",
      orderStatus: f.orderStatus || "",
      shipmentStatus: f.shipmentStatus || "",
      leadSource: f.leadSource || "",
      region_name: f.region || "",
      originCountry: f.originCountry || "",
      originState: f.originState || "",
      originCity: f.originCity || "",
      destinationCountry: f.destinationCountry || "",
      destinationState: f.destinationState || "",
      destinationCity: f.destinationCity || "",
    });

    try {
      const response = await axios.get(
        `${baseAPI}userget/GetShipmentExecutionList?${queryParams.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data?.data || [];
      setExpenseDetailResponse(data);
      setRecordCount(Array.isArray(data) ? data.length : 0);
    } catch (err) {
      console.error("Error fetching shipment execution list:", err);
    }
  };

  // ---------- handlers ----------
  const handleFilterFieldChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilter = () => {
    // update label & close picker (doesn't auto-fetch; user clicks Apply button below)
    setDateRangeLabel(
      `${formatDateDisplay(selectionRange.startDate)} - ${formatDateDisplay(
        selectionRange.endDate
      )}`
    );
    setPickerOpen(false);
  };

  const handleClearDateFilter = () => {
    const todayLocal = new Date();
    const firstDay = new Date(todayLocal.getFullYear(), todayLocal.getMonth(), 1);
    setSelectionRange({ startDate: firstDay, endDate: todayLocal, key: "selection" });
    setDateRangeLabel(`${formatDateDisplay(firstDay)} - ${formatDateDisplay(todayLocal)}`);
    setPickerOpen(false);
    // keep dateAttribute default
    handleFilterFieldChange("dateAttribute", "1");
  };

  const handleTogglePicker = () => setPickerOpen((s) => !s);

  const handleRangeSelect = (ranges) => {
    if (ranges?.selection) setSelectionRange(ranges.selection);
  };

  const resetForm = () => {
    setFilters(initialFilters);
    const todayLocal = new Date();
    const firstDay = new Date(todayLocal.getFullYear(), todayLocal.getMonth(), 1);
    setSelectionRange({ startDate: firstDay, endDate: todayLocal, key: "selection" });
    setDateRangeLabel(`${formatDateDisplay(firstDay)} - ${formatDateDisplay(todayLocal)}`);
    // reload default data
    fetchShipmentList(initialFilters);
  };

  const getLeadDataButton = (e) => {
    e.preventDefault();
    // If user has selected custom dates but label is "Custom Dates", we will send empty dates per previous logic
    fetchShipmentList();
  };

  function encryptRelocationID(Relocation_ID, key) {
    const ciphertext = CryptoJS.AES.encrypt(Relocation_ID.toString(), key).toString();
    return encodeURIComponent(ciphertext);
  }

  function nextPage1(Relocation_ID) {
    const key = "dbbworldwidepvtltd";
    const encryptedRelocationID = encryptRelocationID(Relocation_ID, key);
    const url = `/ViewShipmentDetail?Relocation_ID=${encryptedRelocationID}`;
    window.open(url, "_blank");
  }

  // ---------- initial load ----------
  useEffect(() => {
    fetchServiceTypes();
    fetchVendors();
    fetchEmployeesByDepartment(9, setAssignToResponse); // ops coordinators
    fetchEmployeesByDepartment(3, setAssignToLOResponse); // lead owners / sales
    fetchCountries();
    fetchLeadSources();
    // initial list load
    fetchShipmentList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- render date picker UI ----------
  const renderDateRangePicker = () => {
    return (
      pickerOpen && (
        <div
          style={{
            position: "absolute",
            zIndex: 999,
            border: "solid 1px black",
            marginLeft: "1rem",
            width: "35pc",
            background: "white",
          }}
        >
          <DateRangePicker ranges={[selectionRange]} onChange={handleRangeSelect} />
          <button
            onClick={handleApplyFilter}
            style={{
              marginBottom: "1rem",
              width: "8rem",
              height: "2.5rem",
              borderRadius: "0.5rem",
              fontSize: "13px",
              position: "relative",
              top: "1px",
              left: "19pc",
              backgroundColor: "#5B489D",
              color: "White",
              border: "none",
            }}
          >
            Apply Filter
          </button>
          <button
            onClick={handleClearDateFilter}
            style={{
              marginBottom: "1rem",
              width: "7.5rem",
              height: "2.5rem",
              borderRadius: "0.5rem",
              fontSize: "13px",
              position: "relative",
              top: "1px",
              left: "19pc",
              color: "red",
              backgroundColor: "white",
              border: "none",
              fontWeight: "bold",
            }}
          >
            Clear Filter
          </button>
        </div>
      )
    );
  };

  // ---------- JSX (kept structure & CSS classes unchanged) ----------
  return (
    <div>
      <div className="app-container-viewLeadDetails">
        <MiniDrawer />
        <div style={{ overflowX: "hidden" }} className="container-fluid container-viewLeadDetails">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
            className="country-header"
          >
            <div>
              <h1 style={{ fontSize: "24px" }}>
                <b> Shipment Execution Sheet </b> | <HomeOutlinedIcon /> &gt;
                <a style={{ cursor: "pointer" }} onClick={() => navigate("/ShipmentExecutionSheet")}>
                  Accounts & Finance{" "}
                </a>
                <span style={{ fontSize: "20px" }}>&gt; Shipment Execution Sheet</span>{" "}
              </h1>
            </div>
          </div>

          <div>
            <div className="container-fluid px-3 py-2">
              <div className="mt-2" style={{ display: "flex", justifyContent: "space-between" }}>
                <button className="filterButton">
                  <img src={filterIcon} alt="filter" /> FILTER
                </button>

                <div style={{ display: "flex" }}>
                  <p
                    className="icon"
                    style={{
                      borderColor: "#5B489D",
                      border: "2px solid #5B489D",
                      textAlign: "center",
                      padding: "7px",
                      fontSize: "18px",
                      fontWeight: "bold",
                      height: "3rem",
                      borderRadius: "0.5rem",
                      color: "#6A6A6A",
                      backgroundColor: "white",
                      marginRight: "13px",
                      width: "9rem",
                    }}
                  >
                    {expenseDetailResponse.length === 0 ? <span>0 Records</span> : <span>{expenseDetailResponse.length} Records</span>}
                  </p>
                  <ExcelExport expenseDetailResponse={expenseDetailResponse} />
                </div>
              </div>

              <hr style={{ marginTop: "20px", padding: "0px", marginBottom: "30px" }} />

              <div className="formContainer">
                <div className="container" style={{ padding: "0px" }}>
                  <div className="row">
                    <div className="col fixArrow">
                      <select
                        value={filters.dateAttribute}
                        onChange={(e) => handleFilterFieldChange("dateAttribute", e.target.value)}
                        className="form-control optionSize "
                      >
                        <option value="">Date Attribute</option>
                        <option value="1">Invoice Date</option>
                        <option value="2">Execution Date</option>
                        <option value="3">Booking Date</option>
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>

                    <div className="col fixArrow">
                      <button className="form-control optionSize" onClick={handleTogglePicker} style={{ textAlign: "left" }}>
                        {dateRangeLabel || "Custom Dates"}
                      </button>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                      {renderDateRangePicker()}
                    </div>

                    <div className="col fixArrow">
                      <select
                        id="serviceType"
                        name="serviceType"
                        value={filters.serviceType}
                        onChange={(e) => handleFilterFieldChange("serviceType", e.target.value)}
                        className="form-control optionSize"
                      >
                        <option value="0">Service Type</option>
                        {serviceTypeResponse && serviceTypeResponse.map((item) => (
                          <option key={item.service_type} value={item.service_type}>
                            {item.ServiceTypeName}
                          </option>
                        ))}
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>

                    <div className="col fixArrow">
                      <input
                        type="text"
                        className="form-control optionSize"
                        placeholder="Lead ID / Customer Name / Billing Name"
                        value={filters.searchParam}
                        onChange={(e) => handleFilterFieldChange("searchParam", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col fixArrow">
                      <select
                        id="vendorList"
                        name="vendorList"
                        value={filters.vendor}
                        onChange={(e) => handleFilterFieldChange("vendor", e.target.value)}
                        className="form-control optionSize"
                      >
                        <option value="">Vendor</option>
                        {vendorList.map((exp) => (
                          <option key={exp.Vendor_Id} value={exp.Vendor_Id}>
                            {exp.VendorFirstName} {exp.VendorLastName}
                          </option>
                        ))}
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>

                    <div className="col fixArrow">
                      <select
                        id="petCount"
                        name="petCount"
                        value={filters.petCount}
                        onChange={(e) => handleFilterFieldChange("petCount", e.target.value)}
                        className="form-control optionSize"
                      >
                        <option value="">Pet Count</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                        <option value="6">6</option>
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>

                    <div className="col fixArrow">
                      <select id="chargeableWeight" name="chargeableWeight" className="form-control optionSize">
                        <option>Chargeable Weight</option>
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>

                    <div className="col fixArrow">
                      <select className="form-control optionSize" name="leadtype" onChange={(e) => handleFilterFieldChange("leadType", e.target.value)} value={filters.leadType}>
                        <option disabled value="">Lead Type</option>
                        <option value="0">All</option>
                        <option value="Domestic">Domestic</option>
                        <option value="International">International</option>
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col fixArrow">
                      <select id="shipmentStatus" name="shipmentStatus" value={filters.shipmentStatus} onChange={(e) => handleFilterFieldChange("shipmentStatus", e.target.value)} className="form-control optionSize">
                        <option value="-1">Accounting Status</option>
                        <option value="0">Untouched</option>
                        <option value="1">Open</option>
                        <option value="2">Closed</option>
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>

                    <div className="col fixArrow">
                      <select name="assignedto" onChange={(e) => handleFilterFieldChange("opsCoordinator", e.target.value)} value={filters.opsCoordinator} className="form-control optionSize">
                        <option disabled value="">Ops Coordinator</option>
                        {assignToResponse.map((item) => (
                          <option key={item.customer_id} value={item.customer_id}>
                            {item.name}
                          </option>
                        ))}
                        <option value="All">All</option>
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>

                    <div className="col fixArrow">
                      <select name="assignedtoLO" onChange={(e) => handleFilterFieldChange("leadOwner", e.target.value)} value={filters.leadOwner} className="form-control optionSize">
                        <option disabled value="">Lead Owner</option>
                        {assignToLOResponse.map((item) => (
                          <option key={item.customer_id} value={item.customer_id} className={item.is_available ? "green" : "black"}>
                            {item.name}
                          </option>
                        ))}
                        <option value="All">All</option>
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>

                    <div className="col fixArrow">
                      <select id="orderStatus" name="orderStatus" value={filters.orderStatus} onChange={(e) => handleFilterFieldChange("orderStatus", e.target.value)} className="form-control optionSize">
                        <option value="-1">Order Status</option>
                        <option value="0">Untouched</option>
                        <option value="1">Open</option>
                        <option value="2">Executed</option>
                        <option value="3">Cancelled</option>
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col fixArrow">
                      <select id="region" name="region" value={filters.region} onChange={(e) => handleFilterFieldChange("region", e.target.value)} className="form-control optionSize">
                        <option value=""> Region</option>
                        <option value="1">North</option>
                        <option value="2">East</option>
                        <option value="3">West</option>
                        <option value="4">South</option>
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>

                    <div className="col fixArrow">
                      <select id="originCountrySelect" name="originCountrySelect" onChange={(e) => { handleFilterFieldChange("originCountry", e.target.value); fetchStates(e.target.value, "origin"); }} value={filters.originCountry} className="form-control optionSize" onClick={fetchCountries}>
                        <option value=""> Origin Country</option>
                        {originCountryResponse.map((country) => (
                          <option key={country.id} value={country.id}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>

                    <div className="col fixArrow">
                      <select id="originState" name="originState" value={filters.originState} onChange={(e) => { handleFilterFieldChange("originState", e.target.value); fetchCities(e.target.value, "origin"); }} className="form-control optionSize">
                        <option value=""> Origin State</option>
                        {originStateResponse.map((item) => (
                          <option key={item.state_id} value={item.state_id}>
                            {item.stateName}
                          </option>
                        ))}
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>

                    <div className="col fixArrow">
                      <select id="city" name="city" value={filters.originCity} onChange={(e) => handleFilterFieldChange("originCity", e.target.value)} className="form-control optionSize">
                        <option value=""> Origin City</option>
                        {originCityResponse.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col fixArrow">
                      <select id="leadSource" name="leadSource" value={filters.leadSource} onChange={(e) => handleFilterFieldChange("leadSource", e.target.value)} className="form-control optionSize" onClick={fetchLeadSources}>
                        <option value=""> Lead Source</option>
                        {leadSourceResponse.map((leadsource) => (
                          <option key={leadsource.leadsourceid} value={leadsource.leadsourceid}>
                            {leadsource.LeadSource}
                          </option>
                        ))}
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>

                    <div className="col fixArrow">
                      <select id="destinationCountry" name="destinationCountry" value={filters.destinationCountry} onChange={(e) => { handleFilterFieldChange("destinationCountry", e.target.value); fetchStates(e.target.value, "destination"); }} className="form-control optionSize" onClick={fetchCountries}>
                        <option value=""> Destination Country</option>
                        {originCountryResponse.map((country) => (
                          <option key={country.id} value={country.id}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>

                    <div className="col fixArrow">
                      <select id="state" name="state" value={filters.destinationState} onChange={(e) => { handleFilterFieldChange("destinationState", e.target.value); fetchCities(e.target.value, "destination"); }} className="form-control optionSize">
                        <option value=""> Destination State</option>
                        {destinationStateResponse.map((item) => (
                          <option key={item.state_id} value={item.state_id}>
                            {item.stateName}
                          </option>
                        ))}
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>

                    <div className="col fixArrow">
                      <select id="city" name="city" value={filters.destinationCity} onChange={(e) => handleFilterFieldChange("destinationCity", e.target.value)} className="form-control optionSize">
                        <option value=""> Destination City</option>
                        {destinationCityResponse && destinationCityResponse.length ? (
                          destinationCityResponse.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                            </option>
                          ))
                        ) : (
                          <option value="">No cities</option>
                        )}
                      </select>
                      <span className="arrow" style={{ position: "relative", marginLeft: "-32px", marginTop: "14px", color: "#BFBFBF" }}>
                        &#9660;
                      </span>
                    </div>
                  </div>

                  <hr />

                  <div className="row">
                    <div className="ApplyClearButton">
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={resetForm}
                        style={{
                          marginTop: "12px",
                          border: "none",
                          color: "red",
                          backgroundColor: "white",
                          height: "47px",
                          width: "8rem",
                          fontWeight: "bold",
                        }}
                      >
                        Clear Filter
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary applyButtonFilter"
                        onClick={getLeadDataButton}
                        style={{
                          marginTop: "12px",
                          marginLeft: "13px",
                          paddingTop: "1px",
                          height: "47px",
                          width: "8rem",
                        }}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              </div>


              <div className="expenseList">
                <h2
                  className="Venderheading"
                  style={{
                    fontSize: "22px",
                    paddingLeft: "16px",
                    paddingTop: "7px",
                  }}
                >
                  Shipment Execution Sheet
                </h2>
                <div style={{ overflowX: 'auto', width: '100%' }}>
                  <table style={{ width: '100%', tableLayout: 'auto' }} className="table_expense sscroll">
                    <thead>
                      <tr className="expenseListHeading">
                        <th>S.No.</th>
                        <th>Lead ID</th>
                        <th>Invoice Date</th>
                        <th>Execution Date</th>
                        <th>File Status</th>
                        <th>Accounting Stage</th>
                        <th>Invoice Number</th>
                        <th>Quotation Number</th>
                        <th>Shipment Ref. No.</th>
                        <th>Billing Name</th>
                        <th>Customer Name</th>
                        <th>Lead Owner</th>
                        <th>Operation Coordinator</th>
                        <th>Field Coordinator</th>
                        <th>Shipment Type</th>
                        <th>Origin</th>
                        <th>Destination</th>
                        <th>Vendors</th>
                        <th>Number of Pets</th>
                        <th>Rate</th>
                        <th>Chargeble Weight</th>
                        <th>Cost Of Goods Sold</th>
                        <th>Sales</th>
                        <th>Payment Mode</th>
                        <th>Margin</th>
                        <th>Invoice Status</th>
                        <th>Bills Status</th>

                        <th>Customer Type</th>
                        <th>Sub Category</th>
                        <th>Lead Type</th>
                        <th>Lead Region</th>
                        <th>Email</th>
                        <th>Mobile No</th>
                        <th>Alternate Contact No.</th>
                        <th>Airline Name</th>
                        <th>Mode of Transport</th>
                        <th>Delivery Type</th>
                        <th>Invoice Amount (With GST)</th>
                        <th>Profit %</th>
                        <th>Amount in AED</th>
                        <th>Order Status</th>
                        <th>Stage Name</th>
                        <th>Sub Stage Name</th>
                        <th>Pet Name</th>
                        <th>Pet Dimension</th>
                        <th>Pet Age</th>
                        <th>Pet Type</th>
                        <th>Pet Breed</th>

                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenseDetailResponse !== null ? (
                        expenseDetailResponse.map((expense, index) => (
                          <>
                            <tr key={index + 1}>
                              <td>{index + 1}</td>
                              <td><a onClick={() => nextPage1(expense.Lead_ID)} style={{ fontWeight: "bold", cursor: "pointer" }} >{expense.Lead_ID}</a></td>
                              <td>{formatDateWithTime(expense.Execution_Date)}</td>
                              <td>{formatDateWithTime(expense.Execution_Date)}</td>
                              <td>{expense.File_State}</td>
                              <td>{expense.shipment_stageName}</td>
                              <td>{expense.Invoice_Number}</td>
                              <td>{expense.Quotation_Number}</td>
                              <td>{expense.Shipment_Ref_No}</td>
                              <td>{expense.Billing_Name}</td>
                              <td>{expense.Customer_Name}</td>
                              <td>{expense.Lead_Owner}</td>
                              <td>{expense.Operation_Coordinator}</td>
                              <td>{expense.Field_Coordinator}</td>
                              <td>{expense.Shipment_Type}</td>
                              <td>{expense.Origin}</td>
                              <td>{expense.Destination}</td>
                              <td>{expense.Vendors}</td>
                              <td>{expense.Pieces}</td>
                              <td>{expense.Rate}</td>
                              <td>{expense.Chargeble_Weight}</td>
                              <td>{expense.Cost_Of_Goods_Sold}</td>
                              <td>{expense.Sales}</td>
                              <td>{expense.Payment_Mode}</td>
                              <td>{expense.Margin}</td>
                              <td>{expense.Invoice_Status}</td>
                              <td>{expense.Bills_Status}</td>


                              <td>{expense.customer_type}</td>
                              <td>{expense.sub_category}</td>
                              <td>{expense.lead_type}</td>
                              <td>{expense.lead_region}</td>
                              <td>{expense.Email}</td>
                              <td>{expense.Mobile_No}</td>
                              <td>{expense.alternate_number}</td>
                              <td>{expense.airline_name}</td>
                              <td>{expense.transport_mode}</td>
                              <td>{expense.delivery_type_name}</td>
                              <td>{expense.invoice_amount_with_gst}</td>
                              <td>{expense.ProfitPercent?.toFixed(2)}%</td>
                              <td>{expense.Amount_In_AED}</td>
                              <td>{expense.order_status_name}</td>
                              <td>{expense.stage_name}</td>
                              <td>{expense.sub_stage_name}</td>
                              <td>{expense.pet_Name}</td>
                              <td>{expense.Dimension}</td>
                              <td>{expense.Pet_Age}</td>
                              <td>{expense.PetType}</td>
                              <td>{expense.PetBreedType}</td>


                              <td style={{ padding: '17px' }}>
                                <button
                                  className="text-center"
                                  style={{
                                    width: '35px',
                                    height: '35px',
                                    padding: '0px',
                                    margin: '0px',
                                    border: 'none',
                                  }}
                                >
                                  <SearchIcon
                                    className="icon"
                                    style={{
                                      color: '#6a6a6a',
                                      backgroundColor: '#F1F1F1',
                                      width: '33px',
                                      padding: '7px',
                                      height: '33px',
                                      borderRadius: '5px',
                                    }}
                                  />
                                </button>
                              </td>
                            </tr>
                          </>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="15">No expenses available</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <nav
                  aria-label="Page navigation"
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: "1rem",
                  }}
                >
                  <ul className="pagination">
                    <li className="page-item">
                      <a style={{ cursor: "pointer", fontWeight: 'bold', color: '#5B489D' }}
                        className="page-link"
                      >
                        Previous
                      </a>
                    </li>

                    <li style={{ backgroundColor: '#5B489D' }} className="page-item active">
                      <a style={{ backgroundColor: '#5B489D', border: 'none' }} className="page-link">1</a>
                    </li>
                    <li className="page-item">
                      <a style={{ cursor: "pointer", color: '#5B489D' }}
                        className="page-link"
                      >
                        2
                      </a>
                    </li>
                    <li className="page-item">
                      <a style={{ cursor: "pointer", color: '#5B489D', fontWeight: 'bold' }}
                        className="page-link"
                      >
                        Next
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentExecutionSheet;

